import React, { FC, useState, useEffect, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Loader,
  Option,
  Combobox,
  Button,
  ButtonColor,
  ButtonSize,
} from '@code/innovance-react-ui-kit';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { GlobalBonus, LoanProductPage } from 'src/front/api/apiInstance';
import {
  Container,
  Title,
  Form,
  FormElement,
  FormRow,
  Separator,
} from 'src/front/components/FormStyles';
import { LoanApplicationProductSchema } from 'src/front/common/validations/loanApplication';
import { dictionaryLineFormat, optionFormat } from 'src/front/common/utils/formUtils';
import GlobalBonusForm from './GlobalBonusForm';

interface Props {
  goToNextStep: () => void;
}
interface FormData {
  retailOutlet: Option | null;
  loanType: Option | null;
}
const defaultValues = {
  retailOutlet: null,
  loanType: null,
};
const ProductInformation: FC<Props> = ({ goToNextStep }) => {
  const retailOutlets = usePRM(ReferenceDataType.CREDITSHOPS);
  const loanTypes = usePRM(ReferenceDataType.LOANTYPE);
  const [isGlobalBonusVisible, setIsGlobalBonusVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const { t } = useTranslation();

  const { handleSubmit, watch, setValue, errors, register } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(LoanApplicationProductSchema),
  });

  const getGlobalVisiblity = useCallback(async (): Promise<void> => {
    const { data } = await GlobalBonus.getGlobalBonusVisibility();
    if (data.result === 'OK') {
      setIsGlobalBonusVisible(data.isGlobalBonusVisible || false);
    }
  }, []);
  const registerFields = useCallback(() => {
    register('retailOutlet');
    register('loanType');
  }, [register]);
  useEffect(() => {
    registerFields();
  }, [registerFields]);
  const getData = useCallback(async (): Promise<void> => {
    const { data } = await LoanProductPage.getLoanProductPageData();
    if (data.result === 'OK' && data.pageData) {
      const { loanType, retailOutlet } = data.pageData;
      setValue('loanType', optionFormat(loanType));
      setValue('retailOutlet', optionFormat(retailOutlet));
    }
  }, [setValue]);

  useEffect(() => {
    setIsLoading(true);
    getGlobalVisiblity();
    getData();
    setIsLoading(false);
  }, [getData, getGlobalVisiblity]);

  const handleComboChange = (name: string) => (option: Option | null) => {
    setValue(name, option, { shouldValidate: true, shouldDirty: true });
  };
  const onSubmit = async (): Promise<void> => {
    const { data } = await LoanProductPage.validateLoanProductPageData({
      loanType: dictionaryLineFormat(watch('loanType')),
      retailOutlet: dictionaryLineFormat(watch('retailOutlet')),
    });
    if (data.result === 'OK') {
      goToNextStep();
    }
  };
  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_productInfo_title" />
      </Title>
      {isModalVisible ? <GlobalBonusForm onClose={() => setIsModalVisible(false)} /> : null}
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t(errors?.retailOutlet?.message as string)}
              items={retailOutlets}
              label={t('general_components_shop_label')}
              name="retailOutlet"
              onChange={handleComboChange('retailOutlet')}
              value={watch('retailOutlet')}
              required
              searchable
            />
          </FormElement>
          {watch('retailOutlet')?.value ? (
            <FormElement>
              <Combobox
                errorMessage={t(errors?.loanType?.message as string)}
                items={loanTypes}
                label={t('general_components_loanType_label')}
                name="loanType"
                onChange={handleComboChange('loanType')}
                value={watch('loanType')}
                required
              />
            </FormElement>
          ) : null}
        </FormRow>

        <FormRow>
          {isGlobalBonusVisible && watch('retailOutlet')?.value ? (
            <FormElement>
              <Button
                color={ButtonColor.Secondary}
                onClick={() => setIsModalVisible(true)}
                size={ButtonSize.Medium}
                text={t('general_components_globalBonus_text')}
                type="button"
              />
            </FormElement>
          ) : null}
        </FormRow>
        <Separator />
        <FormRow>
          {watch('retailOutlet')?.value ? (
            <FormElement>
              <Button
                color={ButtonColor.Primary}
                // disabled={formState.isSubmitted}
                size={ButtonSize.Large}
                text={t('general_continue_button')}
                type="submit"
              />
            </FormElement>
          ) : null}
        </FormRow>
      </Form>
    </Container>
  );
};

export default ProductInformation;
